import { Component } from '@theme/component';

const mobileQuery = window.matchMedia('(max-width: 749px)');
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Plays a Shopify-hosted video with separate desktop and mobile files.
 *
 * - Picks the source for the current breakpoint and swaps it when the breakpoint changes.
 * - Autoplays (muted) only while the video is near the viewport and reduced motion is not requested.
 * - Exposes a play/pause toggle so moving media can always be stopped (WCAG 2.2.2).
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video
 * @property {HTMLButtonElement} [toggle]
 *
 * @extends {Component<Refs>}
 */
class GcVideoComponent extends Component {
  requiredRefs = ['video'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  #inView = false;
  #userPaused = false;
  #userStarted = false;

  // Media events arrive from queued tasks, so pauses and plays started by the
  // component are flagged and the flag is consumed in the event handler. Any
  // other play or pause came from the visitor (for example native controls).
  #componentPaused = false;
  #componentPlaying = false;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { video } = this.refs;

    video.addEventListener('playing', this.#handlePlaying, { signal });
    video.addEventListener('play', this.#handlePlay, { signal });
    video.addEventListener('pause', this.#handlePause, { signal });
    video.addEventListener('ended', this.#handlePause, { signal });
    mobileQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
    reducedMotionQuery.addEventListener('change', this.#sync, { signal });

    this.#applySource();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#inView = entry.isIntersecting;
        this.#sync();
      },
      { rootMargin: '150px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#componentPaused = true;
    this.refs.video?.pause();
  }

  get autoplay() {
    return this.hasAttribute('data-autoplay');
  }

  /**
   * Toggles playback from the play/pause button.
   */
  toggle() {
    const { video } = this.refs;
    if (!video) return;

    if (video.paused) {
      this.#userPaused = false;
      this.#userStarted = true;
      this.#play();
    } else {
      this.#userPaused = true;
      video.pause();
    }
  }

  #applySource() {
    const { video } = this.refs;
    if (!video) return;

    const desktop = this.dataset.srcDesktop || '';
    const mobile = this.dataset.srcMobile || '';
    const next = mobileQuery.matches ? mobile || desktop : desktop || mobile;
    if (!next || video.getAttribute('src') === next) return;

    const wasPlaying = !video.paused;
    video.setAttribute('src', next);
    if (wasPlaying) this.#play();
  }

  #handleBreakpointChange = () => {
    this.#applySource();
    this.#sync();
  };

  #sync = () => {
    const { video } = this.refs;
    if (!video) return;

    const wantsAutoplay = this.autoplay && !reducedMotionQuery.matches && !this.#userPaused;
    const shouldPlay = this.#inView && (wantsAutoplay || (this.#userStarted && !this.#userPaused));

    if (shouldPlay && video.paused) {
      this.#play();
    } else if (!shouldPlay && !video.paused) {
      // Out of view, or reduced motion was switched on while autoplaying.
      this.#componentPaused = true;
      video.pause();
    }
  };

  #play() {
    const { video } = this.refs;
    if (!video) return;

    if (!video.getAttribute('src')) this.#applySource();
    if (this.autoplay && !this.#userStarted) video.muted = true;
    if (video.paused) this.#componentPlaying = true;

    const attempt = video.play();
    if (attempt instanceof Promise) {
      attempt.catch(() => {
        // Autoplay can be refused by the browser (data saver, power mode); keep the poster.
        this.#componentPlaying = false;
        this.#setPlaying(false);
      });
    }
  }

  #handlePlaying = () => {
    this.setAttribute('started', '');
    this.#setPlaying(true);
  };

  #handlePlay = () => {
    if (this.#componentPlaying) {
      this.#componentPlaying = false;
      return;
    }
    // Started from the native controls: treat it like the toggle so it is never re-muted.
    this.#userStarted = true;
    this.#userPaused = false;
  };

  #handlePause = () => {
    if (this.#componentPaused) {
      this.#componentPaused = false;
    } else if (!this.refs.video?.ended) {
      this.#userPaused = true;
    }
    this.#setPlaying(false);
  };

  /**
   * @param {boolean} playing
   */
  #setPlaying(playing) {
    this.toggleAttribute('playing', playing);
    const { toggle } = this.refs;
    if (!toggle) return;

    const label = playing ? this.dataset.labelPause : this.dataset.labelPlay;
    if (label) toggle.setAttribute('aria-label', label);
  }
}

if (!customElements.get('gc-video-component')) {
  customElements.define('gc-video-component', GcVideoComponent);
}
